import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { getLostItem, getFoundItem, createClaimRequest } from '../services/itemService';
import { LostItem, FoundItem } from '../types';
import { capitalize, inputClass, labelClass } from '../constants';

type ItemType = 'lost' | 'found';

const ItemDetailPage: React.FC = () => {
  const { type, id } = useParams<{ type: ItemType; id: string }>();
  const itemType: ItemType = type === 'found' ? 'found' : 'lost';
  const { currentUser } = useAuth();

  const [item, setItem] = useState<LostItem | FoundItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimData, setClaimData] = useState({
    description: '',
    proofDescription: ''
  });

  useEffect(() => {
    const fetchItem = async () => {
      if (!id) return;
      try {
        const fetchedItem = itemType === 'lost'
          ? await getLostItem(id)
          : await getFoundItem(id);
        setItem(fetchedItem);
      } catch (error) {
        console.error('Error fetching item:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id, itemType]);

  const isLostItem = (item: LostItem | FoundItem): item is LostItem => 'dateLost' in item;

  const getDate = (item: LostItem | FoundItem) => {
    const dateField = isLostItem(item) ? item.dateLost : item.dateFound;
    return dateField instanceof Date ? dateField : new Date((dateField as any).seconds * 1000);
  };

  const handleClaimSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !item) return;

    setClaimLoading(true);
    try {
      await createClaimRequest({
        itemId: item.id,
        itemType,
        claimantId: currentUser.uid,
        claimantEmail: currentUser.email || '',
        claimantName: currentUser.displayName || 'Anonymous',
        description: claimData.description,
        proofDescription: claimData.proofDescription,
        status: 'pending'
      });
      alert('Claim submitted successfully! You will be notified when it is reviewed.');
      setShowClaimForm(false);
      setClaimData({ description: '', proofDescription: '' });
    } catch (error) {
      console.error('Error submitting claim:', error);
      alert('Failed to submit claim. Please try again.');
    } finally {
      setClaimLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Item Not Found</h2>
        <Link to={`/items/${itemType}`} className="text-primary-600 hover:text-primary-700">
          ← Back to {itemType} items
        </Link>
      </div>
    );
  }

  const isFoundItem = !isLostItem(item);
  const claimButtonText = itemType === 'found' ? 'This is Mine' : 'I Found This';
  const claimFormTitle = itemType === 'found' ? 'Claim This Item' : 'Report Found';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <Link
          to={`/items/${itemType}`}
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to {capitalize(itemType)} Items
        </Link>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Image */}
          <div className="relative h-64 sm:h-80 bg-gray-200">
            {item.imageUrl ? (
              <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full">
                <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            {/* Status badges */}
            <div className="absolute top-4 right-4 flex gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                itemType === 'lost' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {capitalize(itemType)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                item.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {capitalize(item.status)}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{item.title}</h1>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  {capitalize(item.category)}
                </span>
              </div>
              {item.claimed && (
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800 font-medium">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Claimed
                </span>
              )}
            </div>

            {/* Description */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Description</h2>
              <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                <svg className="w-6 h-6 text-gray-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium text-gray-900">{item.location || 'Not specified'}</p>
                </div>
              </div>

              <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                <svg className="w-6 h-6 text-gray-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">{itemType === 'lost' ? 'Date Lost' : 'Date Found'}</p>
                  <p className="font-medium text-gray-900">{format(getDate(item), 'MMMM d, yyyy')}</p>
                </div>
              </div>

              {isFoundItem && (item as FoundItem).turnedInToOffice && (
                <div className="flex items-start p-4 bg-gray-50 rounded-lg sm:col-span-2">
                  <svg className="w-6 h-6 text-gray-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-500">Turned in to Office</p>
                    <p className="font-medium text-gray-900">{(item as FoundItem).officeLocation}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Claim button */}
            {!item.claimed && currentUser && (
              <div className="border-t border-gray-200 pt-6">
                {!showClaimForm ? (
                  <button
                    onClick={() => setShowClaimForm(true)}
                    className="w-full sm:w-auto px-6 py-3 bg-primary-600 text-white rounded-md font-semibold hover:bg-primary-700 transition-colors"
                  >
                    {claimButtonText}
                  </button>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{claimFormTitle}</h3>
                    <form onSubmit={handleClaimSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="description" className={labelClass}>
                          {itemType === 'found' ? 'Why is this yours?' : 'Where did you find it?'} *
                        </label>
                        <textarea
                          id="description"
                          required
                          rows={3}
                          value={claimData.description}
                          onChange={(e) => setClaimData({ ...claimData, description: e.target.value })}
                          className={inputClass}
                          placeholder={itemType === 'found'
                            ? 'Describe the item and why you believe it belongs to you...'
                            : 'Describe where and when you found this item...'}
                        />
                      </div>
                      <div>
                        <label htmlFor="proof" className={labelClass}>
                          {itemType === 'found' ? 'Proof of ownership (optional)' : 'Current location of item'}
                        </label>
                        <textarea
                          id="proof"
                          rows={2}
                          value={claimData.proofDescription}
                          onChange={(e) => setClaimData({ ...claimData, proofDescription: e.target.value })}
                          className={inputClass}
                          placeholder={itemType === 'found'
                            ? 'Any distinguishing features only the owner would know...'
                            : 'Where is the item now? Did you turn it in somewhere?'}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          type="submit"
                          disabled={claimLoading}
                          className="px-6 py-2 bg-primary-600 text-white rounded-md font-semibold hover:bg-primary-700 disabled:opacity-50 transition-colors"
                        >
                          {claimLoading ? 'Submitting...' : 'Submit Claim'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowClaimForm(false)}
                          className="px-6 py-2 border border-gray-300 rounded-md font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {!currentUser && !item.claimed && (
              <div className="border-t border-gray-200 pt-6">
                <p className="text-gray-600">
                  <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                    Sign in
                  </Link>
                  {' '}to claim this item.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailPage;